import React, { useState, useEffect, useMemo } from 'react';
import api from '../../config/api';
import { formatPhoneDisplay } from '../../utils/formatPhoneDisplay';
import AdminAddUserModal from './AdminAddUserModal';
import { FaEdit, FaTrash, FaChevronLeft, FaChevronRight, FaSearch, FaTimes } from 'react-icons/fa';
import { formatBirthDateDisplayUtc } from '../../utils/dateInput';
import { MEMBERSHIP_SEASON } from '../../config/membershipSeason';
import { MEMBER_ACTIVE_SEASONS } from '../../utils/memberStatus';
import {
  buildEmptyMembershipFlagsBySeason,
  buildMembershipFlagsBySeasonFromUser,
  getMembershipFieldsForSeason,
  getMembershipFlagsForSeason,
  mergeJofcFlagsIntoSeasonFlags,
} from '../../utils/membershipFlags';
import {
  buildEmptyMembershipCodesBySeason,
  buildMembershipCodesBySeasonFromUser,
  getMembershipCodesForSeason,
} from '../../utils/membershipCodes';
import { civiliteForApiSave, civiliteToFormSelectValue } from '../../utils/civilite';
import './AdminPages.css';
import './AdminSubscribers.css';

function isResidentItaly(country) {
  const c = String(country || '').trim().toLowerCase();
  return c === 'italie' || c === 'italia' || c === 'italy';
}

/** Saisons avec filtres rapides (liste dépend de la saison). */
const QUICK_FILTER_SEASONS = new Set(['2025-2026', MEMBERSHIP_SEASON]);

function seasonMembershipFlag(user, season, key) {
  return getMembershipFlagsForSeason(user, season)[key] === true;
}

/** Filtres rapides abonnés stade — BASE / FULL / STAR / STAR abo club. */
const MEMBERSHIP_QUICK_FILTERS_SUBSCRIBERS = [
  { id: 'abonneBase', label: 'Abonné BASE', test: (u, s) => seasonMembershipFlag(u, s, 'base') },
  {
    id: 'abonneFull',
    label: 'Abonné FULL',
    test: (u, s) => seasonMembershipFlag(u, s, 'jofcAbonnesFull'),
  },
  { id: 'abonneStar', label: 'Abonné STAR', test: (u, s) => seasonMembershipFlag(u, s, 'star') },
  {
    id: 'starAboClub',
    label: 'Abonné STAR abo club',
    test: (u, s) => seasonMembershipFlag(u, s, 'starAboClub'),
  },
];

const SUBSCRIBER_QUICK_FILTER_IDS = new Set(
  MEMBERSHIP_QUICK_FILTERS_SUBSCRIBERS.map((f) => f.id),
);

/** Filtres rapides import Excel — saison 2025-2026 uniquement. */
const MEMBERSHIP_QUICK_FILTERS_EXCEL = [
  ...MEMBERSHIP_QUICK_FILTERS_SUBSCRIBERS,
  { id: 'abonneMembrePlus', label: 'Abonné membre PLUS', test: (u, s) => seasonMembershipFlag(u, s, 'abonnePlus') },
  { id: 'abonneMembreStandard', label: 'Abonné membre STANDARD', test: (u, s) => seasonMembershipFlag(u, s, 'abonneStandard') },
  { id: 'membrePlus', label: 'Membre PLUS', test: (u, s) => seasonMembershipFlag(u, s, 'plus') },
  {
    id: 'membrePlusSansNewsletter',
    label: 'Membre PLUS désactivé la newsletter',
    test: (u, s) => seasonMembershipFlag(u, s, 'plus') && !seasonMembershipFlag(u, s, 'newsletter'),
  },
  { id: 'membreStandard', label: 'Membre STANDARD', test: (u, s) => seasonMembershipFlag(u, s, 'standard') },
  {
    id: 'plusU14SansNewsletter',
    label: 'Membre PLUS under 14 désactivé la newsletter',
    test: (u, s) => seasonMembershipFlag(u, s, 'plusU14') && !seasonMembershipFlag(u, s, 'newsletter'),
  },
  { id: 'plusU14', label: 'Membre PLUS under 14', test: (u, s) => seasonMembershipFlag(u, s, 'plusU14') },
  { id: 'under5', label: 'Membre under 5', test: (u, s) => seasonMembershipFlag(u, s, 'under5') },
];

/** Filtres rapides cotisation JOFC — saison courante (2026-2027) uniquement. */
const MEMBERSHIP_QUICK_FILTERS_JOFC = [
  { id: 'jofcAdult', label: 'JOFC Adult', test: (u, s) => seasonMembershipFlag(u, s, 'jofcAdult') },
  { id: 'jofcUnder14', label: 'JOFC under 14', test: (u, s) => seasonMembershipFlag(u, s, 'jofcUnder14') },
  { id: 'jofcUnder5', label: 'JOFC Under 5', test: (u, s) => seasonMembershipFlag(u, s, 'jofcUnder5') },
];

const JOFC_QUICK_FILTER_IDS = new Set(MEMBERSHIP_QUICK_FILTERS_JOFC.map((f) => f.id));

const ALL_MEMBERSHIP_QUICK_FILTERS = [
  ...MEMBERSHIP_QUICK_FILTERS_EXCEL,
  ...MEMBERSHIP_QUICK_FILTERS_JOFC,
];

const QUICK_FILTERS_SUMMARY_LABEL = [...QUICK_FILTER_SEASONS].sort().join(', ');

function getQuickFiltersForSeason(season) {
  if (season === MEMBERSHIP_SEASON) {
    return [...MEMBERSHIP_QUICK_FILTERS_SUBSCRIBERS, ...MEMBERSHIP_QUICK_FILTERS_JOFC];
  }
  if (season === '2025-2026') return MEMBERSHIP_QUICK_FILTERS_EXCEL;
  return [];
}

function quickFiltersSectionLabel(season) {
  if (season === MEMBERSHIP_SEASON || season === '2025-2026') return season;
  return QUICK_FILTERS_SUMMARY_LABEL;
}

/** Types d'adhésion JOFC (cotisation) — un seul actif à la fois */
const JOFC_MEMBERSHIP_TYPES = [
  { key: 'jofcAdult', label: 'JOFC Adult' },
  { key: 'jofcUnder14', label: 'JOFC under 14' },
  { key: 'jofcUnder5', label: 'JOFC Under 5' },
];

const SUBSCRIPTION_TRIBUNE_OPTIONS = [
  'Tribuna Sud 1°',
  'Tribuna Sud 2°',
  'Tribuna Nord 1°',
  'Tribuna Nord 2°',
  'Tribuna Family',
  'Tribuna Est Centrale 1°',
  'Tribuna Est Centrale 2°',
  'Tribuna Est Laterale 1°',
  'Tribuna Est Laterale 2°',
  'Tribuna Est 2 Laterale (Nord Est)',
  'Tribuna Est 2 Laterale (Sud Est)',
  'Tribuna Ovest Laterale 1°',
  'Tribuna Ovest Laterale 2°',
  'Tribuna Ovest Centrale 2°',
  'Tribuna Sivori 1°',
  'Tribuna Sivori 2°',
  'Tribuna Boniperti 1°',
  'Tribuna T100',
];

/** Seuils JOFC saison 2026-2027 (alignés sur l'inscription en ligne). */
const JOFC_2026_UNDER5_CUTOFF = new Date('2022-07-01T00:00:00.000Z');
const JOFC_2026_UNDER14_CUTOFF = new Date('2013-07-01T00:00:00.000Z');

function parseBirthDateUtc(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Détermine le type d'adhésion JOFC selon la date de naissance. */
function deriveJofcMembershipTypeFromBirthDate(dateOfBirth) {
  const birthDate = parseBirthDateUtc(dateOfBirth);
  if (!birthDate) return 'jofcAdult';
  if (birthDate > JOFC_2026_UNDER5_CUTOFF) return 'jofcUnder5';
  if (birthDate > JOFC_2026_UNDER14_CUTOFF) return 'jofcUnder14';
  return 'jofcAdult';
}

/** Saisons où l’admin indique que le membre était actif (aligné avec le backend `MEMBER_ACTIVE_SEASONS`). */
function sortActiveSeasonsList(arr) {
  if (!Array.isArray(arr)) return [];
  const set = new Set(arr.filter((s) => MEMBER_ACTIVE_SEASONS.includes(s)));
  return MEMBER_ACTIVE_SEASONS.filter((s) => set.has(s));
}

/** Saisons actives en base ; repli sur `season` (CRM) uniquement si `activeSeasons` est vide (anciens comptes). */
function getActiveSeasonsFromUser(user) {
  const fromDb = Array.isArray(user.activeSeasons) ? user.activeSeasons : [];
  if (fromDb.length > 0) return sortActiveSeasonsList(fromDb);
  if (user.season && MEMBER_ACTIVE_SEASONS.includes(user.season)) {
    return sortActiveSeasonsList([user.season]);
  }
  return [];
}

/** Champ CRM `season` : dernière saison cochée dans l’ordre admin (ex. 2026-2027 si plusieurs). */
function deriveSeasonFromActiveSeasons(activeSeasons) {
  const sorted = sortActiveSeasonsList(activeSeasons);
  if (sorted.length === 0) return null;
  return sorted[sorted.length - 1];
}

function mergedActiveSeasonsForDisplay(user) {
  return getActiveSeasonsFromUser(user);
}

function formatActiveSeasonsCell(user) {
  const sorted = mergedActiveSeasonsForDisplay(user);
  return sorted.length ? sorted.join(', ') : '—';
}

function getSeasonActivationDate(user, season) {
  const raw = user?.seasonActivationDates;
  if (!raw) return null;
  const value = raw instanceof Map ? raw.get(season) : raw[season];
  return value || null;
}

/** Date d’activation admin pour la saison courante (si le membre y est actif). */
function formatCurrentSeasonActivationDate(user, formatDateFn) {
  const activeSeasons = mergedActiveSeasonsForDisplay(user);
  if (!activeSeasons.includes(MEMBERSHIP_SEASON)) return '—';
  const activationDate = getSeasonActivationDate(user, MEMBERSHIP_SEASON);
  return activationDate ? formatDateFn(activationDate) : '—';
}

function getUserActivationSortTime(user) {
  const activeSeasons = mergedActiveSeasonsForDisplay(user);
  if (!activeSeasons.includes(MEMBERSHIP_SEASON)) return 0;
  const activationDate = getSeasonActivationDate(user, MEMBERSHIP_SEASON);
  if (!activationDate) return 0;
  const time = new Date(activationDate).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function formatConsentChoice(value) {
  return value ? '✓ Oui' : '✗ Non';
}

function userNewsletterConsent(user) {
  const activeSeasons = mergedActiveSeasonsForDisplay(user);
  const seasonsToCheck =
    activeSeasons.length > 0 ? activeSeasons : [MEMBERSHIP_SEASON];
  return seasonsToCheck.some(
    (season) => getMembershipFlagsForSeason(user, season).newsletter === true,
  );
}

/** L’utilisateur est rattaché à cette saison (filtre liste). Si `activeSeasons` est renseigné, il fait foi. */
function userMatchesSeasonListFilter(user, seasonParam) {
  if (!seasonParam || seasonParam === 'all') return true;
  const active = Array.isArray(user.activeSeasons) ? user.activeSeasons : [];
  if (seasonParam === 'old') {
    const noCrmSeason = user.season == null || String(user.season).trim() === '';
    return noCrmSeason && active.length === 0;
  }
  if (active.length > 0) return active.includes(seasonParam);
  if (user.season === seasonParam) return true;
  return false;
}

function userIsActiveForSeason(user, seasonParam) {
  return userMatchesSeasonListFilter(user, seasonParam);
}

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [quickFilter, setQuickFilter] = useState('none');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkMemberUpdating, setBulkMemberUpdating] = useState(false);
  const [bulkJofcType, setBulkJofcType] = useState('');
  const [showBulkActiveModal, setShowBulkActiveModal] = useState(false);
  const [bulkActiveSeasons, setBulkActiveSeasons] = useState([]);
  const [filterInactiveFor2026, setFilterInactiveFor2026] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [formData, setFormData] = useState({
    civilite: '',
    firstName: '',
    lastName: '',
    email: '',
    sexe: '',
    dateOfBirth: '',
    phone: '',
    codiceFiscale: '',
    role: 'user',
    isMember: false,
    address: {
      street: '',
      streetNumber: '',
      city: '',
      canton: '',
      postalCode: '',
      country: 'Suisse'
    },
    birthPlace: {
      country: '',
      city: '',
      canton: ''
    },
    identity: {
      type: '',
      number: '',
      expiryDate: '',
      issueDate: '',
      issueCanton: '',
      issueCity: ''
    },
    juventusFanCard: {
      number: '',
      expiryDate: '',
      webticketPIN: ''
    },
    membershipFlagsBySeason: buildEmptyMembershipFlagsBySeason(),
    membershipCodesBySeason: buildEmptyMembershipCodesBySeason(),
    subscription: {
      has: false,
      tribune: '',
      settore: '',
      fila: '',
      posto: ''
    },
    activeSeasons: []
  });

  useEffect(() => {
    fetchUsers();
  }, [selectedSeason, filterInactiveFor2026]);

  const visibleQuickFilters = useMemo(
    () => getQuickFiltersForSeason(selectedSeason),
    [selectedSeason],
  );

  const showSeasonMembershipCodes = selectedSeason === MEMBERSHIP_SEASON;

  useEffect(() => {
    if (quickFilter === 'none') return;
    if (!QUICK_FILTER_SEASONS.has(selectedSeason)) {
      setQuickFilter('none');
      return;
    }
    if (!visibleQuickFilters.some((f) => f.id === quickFilter)) {
      setQuickFilter('none');
    }
  }, [selectedSeason, quickFilter, visibleQuickFilters]);

  const fetchUsers = async ({ silent } = {}) => {
    try {
      if (!silent) setLoading(true);
      const params = filterInactiveFor2026
        ? { season: 'all' }
        : selectedSeason !== 'all'
          ? { season: selectedSeason }
          : {};
      const response = await api.get('/users', { params });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Erreur lors du chargement des utilisateurs');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const formatBoolean = (value) => {
    if (value === true || value === 'true' || value === 'yes' || value === 'Yes' || value === 'Si' || value === 'Oui') {
      return '✓';
    }
    return '✗';
  };

  // Filter users by search term (name or email) and optional quick filter (saison 2025-2026)
  const filteredUsers = useMemo(() => {
    let list = users;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      list = list.filter((user) => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        return fullName.includes(searchLower) || email.includes(searchLower);
      });
    }

    if (filterInactiveFor2026) {
      list = list.filter((user) => !userIsActiveForSeason(user, MEMBERSHIP_SEASON));
      if (selectedSeason !== 'all') {
        list = list.filter((user) => userMatchesSeasonListFilter(user, selectedSeason));
      }
    }

    if (quickFilter !== 'none') {
      const def = visibleQuickFilters.find((f) => f.id === quickFilter);
      if (def) {
        list = list.filter(
          (user) => userMatchesSeasonListFilter(user, selectedSeason) && def.test(user, selectedSeason),
        );
      }
    }

    return list;
  }, [users, searchTerm, quickFilter, selectedSeason, visibleQuickFilters, filterInactiveFor2026]);

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];

    list.sort((a, b) => {
      let compareA;
      let compareB;

      switch (sortBy) {
        case 'activationDate':
          compareA = getUserActivationSortTime(a);
          compareB = getUserActivationSortTime(b);
          break;
        case 'createdAt':
        default:
          compareA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          compareB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
      }

      if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [filteredUsers, sortBy, sortOrder]);

  const selectableUsers = useMemo(
    () => sortedUsers.filter((u) => u.role !== 'admin'),
    [sortedUsers],
  );

  useEffect(() => {
    setSelectedUserIds([]);
    setBulkJofcType('');
    setShowBulkActiveModal(false);
    setBulkActiveSeasons([]);
  }, [selectedSeason, searchTerm, quickFilter, filterInactiveFor2026]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Reset to page 1 when season, search, quick filter, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSeason, searchTerm, quickFilter, filterInactiveFor2026, sortBy, sortOrder]);

  const handleQuickFilterClick = (id) => {
    if (id === 'none') {
      setQuickFilter('none');
      return;
    }
    setSelectedSeason((prev) => {
      if (JOFC_QUICK_FILTER_IDS.has(id) || SUBSCRIBER_QUICK_FILTER_IDS.has(id)) {
        return prev === MEMBERSHIP_SEASON ? prev : MEMBERSHIP_SEASON;
      }
      return prev === '2025-2026' ? prev : '2025-2026';
    });
    setQuickFilter(id);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const headerSelectAllChecked =
    selectableUsers.length > 0 &&
    selectableUsers.every((u) => selectedUserIds.includes(u._id));

  const handleSelectAllFiltered = (e) => {
    if (e.target.checked) {
      setSelectedUserIds(selectableUsers.map((u) => u._id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleToggleUserSelect = (userId, role) => {
    if (role === 'admin') return;
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const openBulkActiveModal = () => {
    const targets = selectableUsers.filter((u) => selectedUserIds.includes(u._id));
    if (targets.length === 0) return;

    const initialSeasons = MEMBER_ACTIVE_SEASONS.includes(selectedSeason)
      ? [selectedSeason]
      : [];
    setBulkActiveSeasons(initialSeasons);
    setShowBulkActiveModal(true);
  };

  const toggleBulkActiveSeason = (seasonLabel) => {
    setBulkActiveSeasons((prev) => {
      if (prev.includes(seasonLabel)) {
        return prev.filter((s) => s !== seasonLabel);
      }
      return sortActiveSeasonsList([...prev, seasonLabel]);
    });
  };

  const mergeUserActiveSeasons = (user, seasonsToAdd) => {
    const existing = getActiveSeasonsFromUser(user);
    return sortActiveSeasonsList([...existing, ...seasonsToAdd]);
  };

  const handleBulkConfirmActive = async () => {
    const seasons = sortActiveSeasonsList(bulkActiveSeasons);
    if (seasons.length === 0) {
      alert('Sélectionnez au moins une saison.');
      return;
    }

    const targets = selectableUsers.filter((u) => selectedUserIds.includes(u._id));
    if (targets.length === 0) return;

    const seasonsLabel = seasons.join(', ');
    const appliesJofcMembershipType = seasons.includes(MEMBERSHIP_SEASON);
    const confirmLines = [
      `Confirmer : définir ${targets.length} utilisateur(s) comme ACTIFS (membre) pour les saisons : ${seasonsLabel} ?`,
      'Les saisons déjà actives sont conservées.',
    ];
    if (appliesJofcMembershipType) {
      confirmLines.push(
        '',
        `Pour la saison ${MEMBERSHIP_SEASON}, le type d'adhésion JOFC sera défini automatiquement selon la date de naissance :`,
        '• né après le 01/07/2022 → JOFC Under 5',
        '• né après le 01/07/2013 → JOFC under 14',
        '• sinon → JOFC Adult',
      );
    }
    if (!window.confirm(confirmLines.join('\n'))) {
      return;
    }

    setBulkMemberUpdating(true);
    let ok = 0;
    const failures = [];
    await Promise.all(
      targets.map(async (u) => {
        try {
          const activeSeasons = mergeUserActiveSeasons(u, seasons);
          const updatePayload = {
            isMember: true,
            activeSeasons,
            season: deriveSeasonFromActiveSeasons(activeSeasons),
          };
          if (appliesJofcMembershipType) {
            const jofcTypeKey = deriveJofcMembershipTypeFromBirthDate(u.dateOfBirth);
            const membershipFlagsBySeason = buildMembershipFlagsBySeasonFromUser(u);
            membershipFlagsBySeason[MEMBERSHIP_SEASON] = mergeJofcFlagsIntoSeasonFlags(
              membershipFlagsBySeason[MEMBERSHIP_SEASON],
              jofcTypeKey,
            );
            updatePayload.membershipFlagsBySeason = membershipFlagsBySeason;
          }
          await api.put(`/users/${u._id}`, updatePayload);
          ok += 1;
        } catch (err) {
          failures.push(u.email || u._id);
        }
      }),
    );
    setBulkMemberUpdating(false);
    setShowBulkActiveModal(false);
    setBulkActiveSeasons([]);
    setSelectedUserIds([]);
    await fetchUsers({ silent: true });

    if (failures.length > 0) {
      alert(
        `${ok} utilisateur(s) mis à jour.\nÉchecs (${failures.length}) : ${failures.slice(0, 8).join(', ')}${failures.length > 8 ? '…' : ''}`,
      );
    } else {
      let successMsg = `${ok} utilisateur(s) actifs pour la saison${seasons.length > 1 ? 's' : ''} : ${seasonsLabel}.`;
      if (appliesJofcMembershipType) {
        successMsg += `\nType d'adhésion JOFC (${MEMBERSHIP_SEASON}) appliqué selon la date de naissance.`;
      }
      alert(successMsg);
    }
  };

  const handleBulkSetIsMember = async (isMember) => {
    const targets = selectableUsers.filter((u) => selectedUserIds.includes(u._id));
    if (targets.length === 0) return;

    const actionLabel = 'INACTIFS (non-membre)';
    if (
      !window.confirm(
        `Confirmer : définir ${targets.length} utilisateur(s) comme ${actionLabel} ?\n\nLes comptes administrateur ne sont pas concernés.`,
      )
    ) {
      return;
    }

    setBulkMemberUpdating(true);
    let ok = 0;
    const failures = [];
    await Promise.all(
      targets.map(async (u) => {
        try {
          await api.put(`/users/${u._id}`, { isMember: false });
          ok += 1;
        } catch (err) {
          failures.push(u.email || u._id);
        }
      }),
    );
    setBulkMemberUpdating(false);
    setSelectedUserIds([]);
    await fetchUsers({ silent: true });

    if (failures.length > 0) {
      alert(
        `${ok} utilisateur(s) mis à jour.\nÉchecs (${failures.length}) : ${failures.slice(0, 8).join(', ')}${failures.length > 8 ? '…' : ''}`,
      );
    } else {
      alert(`${ok} utilisateur(s) mis à jour avec succès.`);
    }
  };

  const handleBulkSetJofcMembershipType = async () => {
    const typeKey = bulkJofcType;
    if (!typeKey) return;

    const targets = selectableUsers.filter((u) => selectedUserIds.includes(u._id));
    if (targets.length === 0) return;

    const typeLabel =
      JOFC_MEMBERSHIP_TYPES.find((t) => t.key === typeKey)?.label ?? typeKey;
    if (
      !window.confirm(
        `Confirmer : définir le type d'adhésion « ${typeLabel} » pour ${targets.length} utilisateur(s) ?\n\nLes autres types JOFC (Adult / under 14 / Under 5) seront désactivés pour ces comptes.`,
      )
    ) {
      return;
    }

    setBulkMemberUpdating(true);
    let ok = 0;
    const failures = [];
    await Promise.all(
      targets.map(async (u) => {
        try {
          const membershipFlagsBySeason = buildMembershipFlagsBySeasonFromUser(u);
          membershipFlagsBySeason[MEMBERSHIP_SEASON] = mergeJofcFlagsIntoSeasonFlags(
            membershipFlagsBySeason[MEMBERSHIP_SEASON],
            typeKey,
          );
          await api.put(`/users/${u._id}`, { membershipFlagsBySeason });
          ok += 1;
        } catch (err) {
          failures.push(u.email || u._id);
        }
      }),
    );
    setBulkMemberUpdating(false);
    setSelectedUserIds([]);
    setBulkJofcType('');
    await fetchUsers({ silent: true });

    if (failures.length > 0) {
      alert(
        `${ok} utilisateur(s) mis à jour.\nÉchecs (${failures.length}) : ${failures.slice(0, 8).join(', ')}${failures.length > 8 ? '…' : ''}`,
      );
    } else {
      alert(`${ok} utilisateur(s) : type d'adhésion « ${typeLabel} » appliqué.`);
    }
  };

  const handleViewDetails = (user) => {
    setEditingUser(user);
    setIsEditMode(false);
    setFormData({
      civilite: civiliteToFormSelectValue(user.civilite, user.sexe),
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      sexe: user.sexe || '',
      dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
      phone: user.phone || '',
      codiceFiscale: user.codiceFiscale || '',
      role: user.role || 'user',
      isMember: user.isMember || false,
      address: {
        street: user.address?.street || '',
        streetNumber: user.address?.streetNumber || '',
        city: user.address?.city || '',
        canton: user.address?.canton || '',
        postalCode: user.address?.postalCode || '',
        country: user.address?.country || 'Suisse'
      },
      birthPlace: {
        country: user.birthPlace?.country || '',
        city: user.birthPlace?.city || '',
        canton: user.birthPlace?.canton || ''
      },
      identity: {
        type: user.identity?.type || '',
        number: user.identity?.number || '',
        expiryDate: user.identity?.expiryDate ? new Date(user.identity.expiryDate).toISOString().split('T')[0] : '',
        issueDate: user.identity?.issueDate ? new Date(user.identity.issueDate).toISOString().split('T')[0] : '',
        issueCanton: user.identity?.issueCanton || '',
        issueCity: user.identity?.issueCity || ''
      },
      juventusFanCard: {
        number:
          user.juventusFanCard?.number !== undefined &&
          user.juventusFanCard?.number !== null &&
          `${user.juventusFanCard.number}` !== ''
            ? String(user.juventusFanCard.number).trim()
            : '',
        expiryDate: user.juventusFanCard?.expiryDate
          ? new Date(user.juventusFanCard.expiryDate).toISOString().split('T')[0]
          : '',
        webticketPIN: user.juventusFanCard?.webticketPIN ?? ''
      },
      membershipFlagsBySeason: buildMembershipFlagsBySeasonFromUser(user),
      membershipCodesBySeason: buildMembershipCodesBySeasonFromUser(user),
      subscription: {
        has: user.subscription?.has || false,
        tribune: user.subscription?.tribune || '',
        settore: user.subscription?.settore || '',
        fila: user.subscription?.fila || '',
        posto: user.subscription?.posto || ''
      },
      activeSeasons: mergedActiveSeasonsForDisplay(user)
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isResidentItaly(formData.address.country) && !formData.codiceFiscale.trim()) {
      alert('Le Codice fiscale est obligatoire pour les résidents en Italie');
      return;
    }
    try {
      const activeSeasons = sortActiveSeasonsList(formData.activeSeasons || []);
      const updateData = {
        ...formData,
        civilite: civiliteForApiSave(formData.civilite),
        codiceFiscale: formData.codiceFiscale.trim() || '',
        activeSeasons,
        season: deriveSeasonFromActiveSeasons(activeSeasons),
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : null,
        identity: {
          ...formData.identity,
          expiryDate: formData.identity.expiryDate ? new Date(formData.identity.expiryDate) : null,
          issueDate: formData.identity.issueDate ? new Date(formData.identity.issueDate) : null
        }
      };
      updateData.juventusFanCard = {
        ...(editingUser.juventusFanCard || {}),
        number: (formData.juventusFanCard?.number ?? '').trim(),
        webticketPIN: (formData.juventusFanCard?.webticketPIN ?? '').trim(),
        expiryDate: formData.juventusFanCard?.expiryDate
          ? new Date(formData.juventusFanCard.expiryDate)
          : null
      };
      const { data } = await api.put(`/users/${editingUser._id}`, updateData);
      if (data?.user) {
        setEditingUser(data.user);
      }
      alert('Utilisateur mis à jour avec succès');
      setIsEditMode(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur? Cette action est irréversible.')) return;
    
    try {
      await api.delete(`/users/${editingUser._id}`);
      alert('Utilisateur supprimé avec succès');
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      civilite: '',
      firstName: '',
      lastName: '',
      email: '',
      sexe: '',
      dateOfBirth: '',
      phone: '',
      codiceFiscale: '',
      role: 'user',
      isMember: false,
      address: {
        street: '',
        streetNumber: '',
        city: '',
        canton: '',
        postalCode: '',
        country: 'Suisse'
      },
      birthPlace: {
        country: '',
        city: '',
        canton: ''
      },
      identity: {
        type: '',
        number: '',
        expiryDate: '',
        issueDate: '',
        issueCanton: '',
        issueCity: ''
      },
      juventusFanCard: {
        number: '',
        expiryDate: '',
        webticketPIN: ''
      },
      membershipFlagsBySeason: buildEmptyMembershipFlagsBySeason(),
      membershipCodesBySeason: buildEmptyMembershipCodesBySeason(),
      subscription: {
        has: false,
        tribune: '',
        settore: '',
        fila: '',
        posto: ''
      },
      activeSeasons: []
    });
    setEditingUser(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [field]: value
        }
      });
    } else if (name.startsWith('birthPlace.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        birthPlace: {
          ...formData.birthPlace,
          [field]: value
        }
      });
    } else if (name.startsWith('identity.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        identity: {
          ...formData.identity,
          [field]: value
        }
      });
    } else if (name.startsWith('juventusFanCard.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        juventusFanCard: {
          ...formData.juventusFanCard,
          [field]: value
        }
      });
    } else if (name.startsWith('subscription.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        subscription: {
          ...formData.subscription,
          [field]: type === 'checkbox' ? checked : value
        }
      });
    } else if (name.startsWith('membershipFlagsBySeason.')) {
      const parts = name.split('.');
      const seasonKey = parts[1];
      const field = parts[2];
      if (!MEMBER_ACTIVE_SEASONS.includes(seasonKey) || !field) return;
      setFormData({
        ...formData,
        membershipFlagsBySeason: {
          ...formData.membershipFlagsBySeason,
          [seasonKey]: {
            ...(formData.membershipFlagsBySeason?.[seasonKey] || {}),
            [field]: type === 'checkbox' ? checked : value,
          },
        },
      });
    } else if (name.startsWith('membershipCodesBySeason.')) {
      const parts = name.split('.');
      const seasonKey = parts[1];
      const field = parts[2];
      if (!MEMBER_ACTIVE_SEASONS.includes(seasonKey) || !field) return;
      setFormData({
        ...formData,
        membershipCodesBySeason: {
          ...formData.membershipCodesBySeason,
          [seasonKey]: {
            ...(formData.membershipCodesBySeason?.[seasonKey] || {}),
            [field]: value,
          },
        },
      });
    } else if (name.startsWith('activeSeasons.')) {
      const seasonKey = name.slice('activeSeasons.'.length);
      if (!MEMBER_ACTIVE_SEASONS.includes(seasonKey)) return;
      const prev = Array.isArray(formData.activeSeasons) ? formData.activeSeasons : [];
      const next = checked
        ? sortActiveSeasonsList([...prev, seasonKey])
        : prev.filter((s) => s !== seasonKey);
      setFormData({
        ...formData,
        activeSeasons: next,
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const renderConsentsSection = (user) => (
    <section className="detail-section">
      <h3>Consentements et communications</h3>
      <div className="detail-grid">
        <div className="detail-item">
          <label>Marketing:</label>
          <span style={{ color: user.consents?.marketing ? '#27ae60' : '#e74c3c' }}>
            {formatConsentChoice(user.consents?.marketing)}
          </span>
        </div>
        <div className="detail-item">
          <label>Promotion / profilage:</label>
          <span style={{ color: user.consents?.profiling ? '#27ae60' : '#e74c3c' }}>
            {formatConsentChoice(user.consents?.profiling)}
          </span>
        </div>
        <div className="detail-item">
          <label>Droits d&apos;image:</label>
          <span style={{ color: user.consents?.imageRights ? '#27ae60' : '#e74c3c' }}>
            {formatConsentChoice(user.consents?.imageRights)}
          </span>
        </div>
        <div className="detail-item">
          <label>Newsletter:</label>
          <span style={{ color: userNewsletterConsent(user) ? '#27ae60' : '#e74c3c' }}>
            {formatConsentChoice(userNewsletterConsent(user))}
          </span>
        </div>
        {user.isMinor && (
          <div className="detail-item">
            <label>Autorisation parentale:</label>
            <span style={{ color: user.consents?.minorAuthorization ? '#27ae60' : '#e74c3c' }}>
              {formatConsentChoice(user.consents?.minorAuthorization)}
            </span>
          </div>
        )}
        <div className="detail-item">
          <label>Conditions générales:</label>
          <span style={{ color: user.consents?.termsAccepted ? '#27ae60' : '#e74c3c' }}>
            {formatConsentChoice(user.consents?.termsAccepted)}
          </span>
        </div>
      </div>
    </section>
  );

  const renderParentSection = (user) => {
    if (!user.isMinor) return null;

    const registered = user.parentUser;
    const embedded = user.parentInfo;
    if (!registered && !embedded) return null;

    const parent = registered || embedded;
    const birthCountry = registered
      ? parent.birthPlace?.country
      : parent.birthCountry;
    const birthCanton = registered
      ? parent.birthPlace?.canton
      : parent.birthCanton;
    const birthCity = registered
      ? parent.birthPlace?.city
      : parent.birthCity;

    return (
      <section className="detail-section">
        <h3>Informations parent (mineur)</h3>
        {registered && (
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#27ae60', fontWeight: 600 }}>
            ✓ Parent enregistré dans le système
          </p>
        )}
        {!registered && embedded && (
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#666' }}>
            Informations saisies lors de l&apos;inscription du mineur.
          </p>
        )}
        <div className="detail-grid">
          <div className="detail-item">
            <label>Nom:</label>
            <span>{parent.lastName || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Prénom:</label>
            <span>{parent.firstName || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Email:</label>
            <span>{parent.email || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Téléphone:</label>
            <span>{formatPhoneDisplay(parent.phone)}</span>
          </div>
          {parent.sexe && (
            <div className="detail-item">
              <label>Sexe:</label>
              <span>{parent.sexe}</span>
            </div>
          )}
          <div className="detail-item">
            <label>Date de naissance:</label>
            <span>
              {parent.dateOfBirth
                ? formatBirthDateDisplayUtc(parent.dateOfBirth, 'fr-CH') || '—'
                : '—'}
            </span>
          </div>
          <div className="detail-item">
            <label>Pays de naissance:</label>
            <span>{birthCountry || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Canton/Province/Département de naissance:</label>
            <span>{birthCanton || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Ville de naissance:</label>
            <span>{birthCity || '—'}</span>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <div>
            <h1>Gestion des Utilisateurs</h1>
            {!loading && (
              <p
                style={{
                  margin: '0.35rem 0 0',
                  color: '#555',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                {users.length} utilisateur{users.length !== 1 ? 's' : ''}
                {filteredUsers.length !== users.length && (
                  <span style={{ marginLeft: '0.5rem', color: '#666', fontWeight: 400 }}>
                    ({filteredUsers.length} affiché
                    {filteredUsers.length !== 1 ? 's' : ''} après filtrage)
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="header-actions">
            <div className="season-selector" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="season-select">Saison:</label>
                <select
                  id="season-select"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  style={{ padding: '8px', fontSize: '14px' }}
                >
                  <option value="all">Toutes les saisons</option>
                  <option value="old">Anciennes données</option>
                  <option value="2024-2025">2024-2025</option>
                  <option value="2025-2026">2025-2026</option>
                  <option value="2026-2027">2026-2027</option>
                </select>
              </div>
              <label
                htmlFor="inactive-2026-filter"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  id="inactive-2026-filter"
                  type="checkbox"
                  checked={filterInactiveFor2026}
                  onChange={(e) => setFilterInactiveFor2026(e.target.checked)}
                />
                Non actif {MEMBERSHIP_SEASON}
              </label>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{ 
          marginBottom: '1.5rem', 
          padding: '1rem', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            maxWidth: '600px'
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <FaSearch style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#666',
                fontSize: '1rem'
              }} />
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 2.5rem 0.75rem 2.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3498db'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Effacer la recherche"
                >
                  <FaTimes />
                </button>
              )}
            </div>
            {(searchTerm || quickFilter !== 'none' || filterInactiveFor2026) && (
              <div style={{ 
                fontSize: '0.9rem', 
                color: '#666',
                whiteSpace: 'nowrap'
              }}>
                {filteredUsers.length} résultat(s)
                {filterInactiveFor2026 && (
                  <span style={{ marginLeft: '0.35rem', color: '#e67e22' }}>
                    (non actif {MEMBERSHIP_SEASON})
                  </span>
                )}
                {quickFilter !== 'none' && (
                  <span style={{ marginLeft: '0.35rem', color: '#3498db' }}>
                    {`(filtre ${
                      ALL_MEMBERSHIP_QUICK_FILTERS.find((f) => f.id === quickFilter)?.label ?? ''
                    })`}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filtres rapides (saison parmi import Excel) */}
        <div
          style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            background: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: '0.75rem',
              color: '#2c3e50',
            }}
          >
            Filtres rapides
            {visibleQuickFilters.length > 0
              ? ` (saison ${quickFiltersSectionLabel(selectedSeason)})`
              : ` (saisons ${QUICK_FILTERS_SUMMARY_LABEL})`}
            :
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => handleQuickFilterClick('none')}
              style={{
                padding: '0.45rem 0.75rem',
                fontSize: '0.85rem',
                borderRadius: '6px',
                border: '1px solid #bdc3c7',
                background: quickFilter === 'none' ? '#ecf0f1' : '#fff',
                cursor: 'pointer',
                fontWeight: quickFilter === 'none' ? 600 : 400,
              }}
            >
              Tous (filtre)
            </button>
            {visibleQuickFilters.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => handleQuickFilterClick(f.id)}
                style={{
                  padding: '0.45rem 0.75rem',
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  border:
                    quickFilter === f.id
                      ? '2px solid #3498db'
                      : '1px solid #bdc3c7',
                  background: quickFilter === f.id ? '#3498db' : '#fff',
                  color: quickFilter === f.id ? '#fff' : '#2c3e50',
                  cursor: 'pointer',
                  fontWeight: quickFilter === f.id ? 600 : 400,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
            alignItems: 'center',
          }}
        >
          {selectedUserIds.length > 0 ? (
            <div
              className="admin-users-bulk-selection"
              style={{
                flex: '1 1 auto',
                minWidth: '220px',
                padding: '0.65rem 1rem',
                background: '#e8f4fc',
                borderRadius: '8px',
                border: '1px solid #3498db',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <span>
                <strong>{selectedUserIds.length}</strong> utilisateur(s) sélectionné(s)
              </span>
              <button
                type="button"
                disabled={bulkMemberUpdating}
                onClick={openBulkActiveModal}
                style={{
                  padding: '0.45rem 0.85rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#27ae60',
                  color: '#fff',
                  cursor: bulkMemberUpdating ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  opacity: bulkMemberUpdating ? 0.6 : 1,
                }}
              >
                Définir actif
              </button>
              <button
                type="button"
                disabled={bulkMemberUpdating}
                onClick={() => handleBulkSetIsMember(false)}
                style={{
                  padding: '0.45rem 0.85rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#e67e22',
                  color: '#fff',
                  cursor: bulkMemberUpdating ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  opacity: bulkMemberUpdating ? 0.6 : 1,
                }}
              >
                Définir inactif
              </button>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  flexWrap: 'wrap',
                }}
              >
                <label
                  htmlFor="bulk-jofc-type"
                  style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2c3e50' }}
                >
                  Type d&apos;adhésion :
                </label>
                <select
                  id="bulk-jofc-type"
                  value={bulkJofcType}
                  disabled={bulkMemberUpdating}
                  onChange={(e) => setBulkJofcType(e.target.value)}
                  style={{
                    padding: '0.4rem 0.5rem',
                    borderRadius: '6px',
                    border: '1px solid #bdc3c7',
                    fontSize: '0.85rem',
                    minWidth: '11rem',
                  }}
                >
                  <option value="">— Choisir —</option>
                  {JOFC_MEMBERSHIP_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={bulkMemberUpdating || !bulkJofcType}
                  onClick={handleBulkSetJofcMembershipType}
                  style={{
                    padding: '0.45rem 0.85rem',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#8e44ad',
                    color: '#fff',
                    cursor:
                      bulkMemberUpdating || !bulkJofcType ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    opacity: bulkMemberUpdating || !bulkJofcType ? 0.6 : 1,
                  }}
                >
                  Appliquer le type
                </button>
              </span>
              <button
                type="button"
                disabled={bulkMemberUpdating}
                onClick={() => {
                  setSelectedUserIds([]);
                  setBulkJofcType('');
                  setShowBulkActiveModal(false);
                  setBulkActiveSeasons([]);
                }}
                style={{
                  padding: '0.45rem 0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #bdc3c7',
                  background: '#fff',
                  cursor: bulkMemberUpdating ? 'not-allowed' : 'pointer',
                }}
              >
                Effacer la sélection
              </button>
              {bulkMemberUpdating && (
                <span style={{ fontSize: '0.9rem', color: '#34495e' }}>Mise à jour…</span>
              )}
            </div>
          ) : (
            <span style={{ flex: '1 1 auto' }} />
          )}
          <button
            type="button"
            className="btn-submit"
            onClick={() => setShowAddUserModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              flex: '0 0 auto',
              width: 'auto',
              minWidth: 'auto',
            }}
          >
            Ajouter un utilisateur
          </button>
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="checkbox-col" title="Tous les utilisateurs du filtre actuel (hors admins)">
                    <input
                      type="checkbox"
                      checked={headerSelectAllChecked}
                      onChange={handleSelectAllFiltered}
                      disabled={selectableUsers.length === 0 || bulkMemberUpdating}
                      aria-label="Tout sélectionner"
                    />
                  </th>
                  <th>Civilité</th>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Date de naissance</th>
                  <th>Pays de naissance</th>
                  <th>Canton/Province/Département de naissance</th>
                  <th>Ville de naissance</th>
                  <th>Document</th>
                  <th>Numéro de document</th>
                  <th>Validité du document</th>
                  <th>Date d&apos;émission</th>
                  <th>Canton/Département d&apos;émission</th>
                  <th>Commune/localité d&apos;émission</th>
                  <th>Sexe</th>
                  <th>Adresse de domicile</th>
                  <th>Code Postal</th>
                  <th>Ville de domicile</th>
                  <th>Canton/Province/Département</th>
                  <th>Pays de domicile</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  {showSeasonMembershipCodes && <th>e-card</th>}
                  {showSeasonMembershipCodes && <th>Code PNR</th>}
                  <th>Tribune</th>
                  <th>Secteur</th>
                  <th>Rangée</th>
                  <th>Place</th>
                  <th>Rôle</th>
                  <th>Membre actif</th>
                  <th>Saisons actives</th>
                  <th className="sortable" onClick={() => handleSort('activationDate')}>
                    Date d&apos;activation{' '}
                    {sortBy === 'activationDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('createdAt')}>
                    Date d&apos;inscription{' '}
                    {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => {
                  const addressStr = user.address?.street && user.address?.streetNumber 
                    ? `${user.address.street} ${user.address.streetNumber}`.trim()
                    : user.address?.street || '—';
                  
                  return (
                    <tr key={user._id} className={selectedUserIds.includes(user._id) ? 'selected' : ''}>
                      <td className="checkbox-col">
                        {user.role === 'admin' ? (
                          <span className="text-muted" title="Non sélectionnable (administrateur)">
                            —
                          </span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user._id)}
                            onChange={() => handleToggleUserSelect(user._id, user.role)}
                            disabled={bulkMemberUpdating}
                            aria-label={`Sélectionner ${user.firstName} ${user.lastName}`}
                          />
                        )}
                      </td>
                      <td>{user.civilite || '—'}</td>
                      <td>{user.lastName || '—'}</td>
                      <td>{user.firstName || '—'}</td>
                      <td>
                        {user.dateOfBirth
                          ? formatBirthDateDisplayUtc(user.dateOfBirth, 'fr-CH') || '—'
                          : '—'}
                      </td>
                      <td>{user.birthPlace?.country || '—'}</td>
                      <td>{user.birthPlace?.canton || '—'}</td>
                      <td>{user.birthPlace?.city || '—'}</td>
                      <td>{user.identity?.type || '—'}</td>
                      <td>{user.identity?.number || '—'}</td>
                      <td>{user.identity?.expiryDate ? formatDate(user.identity.expiryDate) : '—'}</td>
                      <td>{user.identity?.issueDate ? formatDate(user.identity.issueDate) : '—'}</td>
                      <td>{user.identity?.issueCanton || '—'}</td>
                      <td>{user.identity?.issueCity || '—'}</td>
                      <td>{user.sexe === 'Homme' ? 'Masculin' : user.sexe === 'Femme' ? 'Féminin' : user.sexe || '—'}</td>
                      <td>{addressStr}</td>
                      <td>{user.address?.postalCode || '—'}</td>
                      <td>{user.address?.city || '—'}</td>
                      <td>{user.address?.canton || '—'}</td>
                      <td>{user.address?.country || '—'}</td>
                      <td>{formatPhoneDisplay(user.phone)}</td>
                      <td>{user.email || '—'}</td>
                      {showSeasonMembershipCodes && (
                        <td>{getMembershipCodesForSeason(user, MEMBERSHIP_SEASON).ecardCode || '—'}</td>
                      )}
                      {showSeasonMembershipCodes && (
                        <td>{getMembershipCodesForSeason(user, MEMBERSHIP_SEASON).pnrCode || '—'}</td>
                      )}
                      <td>{user.subscription?.tribune || '—'}</td>
                      <td>{user.subscription?.settore || '—'}</td>
                      <td>{user.subscription?.fila || '—'}</td>
                      <td>{user.subscription?.posto || '—'}</td>
                      <td>
                        <span className={`role ${user.role}`}>
                          {user.role === 'admin' ? 'Admin' : 'Utilisateur'}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${user.isMember ? 'published' : 'draft'}`}>
                          {user.isMember ? 'Oui' : 'Non'}
                        </span>
                      </td>
                      <td style={{ maxWidth: '200px', whiteSpace: 'normal' }} title={formatActiveSeasonsCell(user)}>
                        {formatActiveSeasonsCell(user)}
                      </td>
                      <td>{formatCurrentSeasonActivationDate(user, formatDate)}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td className="actions">
                        <button 
                          className="btn-edit"
                          onClick={() => handleViewDetails(user)}
                          title="Voir/Modifier les détails"
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Précédent
                </button>
                
                <div className="pagination-info">
                  Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                  {' '}(Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(endIndex, sortedUsers.length)}</strong> sur <strong>{sortedUsers.length}</strong> utilisateur(s))
                  {searchTerm && ` sur ${users.length} au total`}
                </div>
                
                <button
                  className="pagination-btn"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Suivant <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        )}

        {showBulkActiveModal && (
          <div
            className="modal-overlay"
            onClick={() => {
              if (!bulkMemberUpdating) {
                setShowBulkActiveModal(false);
                setBulkActiveSeasons([]);
              }
            }}
          >
            <div
              className="modal-content"
              style={{ maxWidth: '420px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Définir actif — saisons</h2>
                <button
                  type="button"
                  className="btn-close"
                  disabled={bulkMemberUpdating}
                  onClick={() => {
                    setShowBulkActiveModal(false);
                    setBulkActiveSeasons([]);
                  }}
                >
                  ×
                </button>
              </div>
              <p style={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#555' }}>
                Choisissez les saisons pour lesquelles{' '}
                <strong>{selectedUserIds.length}</strong> utilisateur(s) seront actifs.
                Les saisons déjà enregistrées pour chaque membre sont conservées.
                {bulkActiveSeasons.includes(MEMBERSHIP_SEASON) && (
                  <>
                    {' '}
                    Pour <strong>{MEMBERSHIP_SEASON}</strong>, le type JOFC (Adult / under 14 /
                    Under 5) sera choisi automatiquement selon la date de naissance.
                  </>
                )}
              </p>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.65rem',
                  marginBottom: '1.25rem',
                }}
              >
                {MEMBER_ACTIVE_SEASONS.map((seasonLabel) => (
                  <label
                    key={seasonLabel}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: bulkMemberUpdating ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={bulkActiveSeasons.includes(seasonLabel)}
                      disabled={bulkMemberUpdating}
                      onChange={() => toggleBulkActiveSeason(seasonLabel)}
                    />
                    {seasonLabel}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  disabled={bulkMemberUpdating}
                  onClick={() => {
                    setShowBulkActiveModal(false);
                    setBulkActiveSeasons([]);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: '1px solid #bdc3c7',
                    background: '#fff',
                    cursor: bulkMemberUpdating ? 'not-allowed' : 'pointer',
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={bulkMemberUpdating || bulkActiveSeasons.length === 0}
                  onClick={handleBulkConfirmActive}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#27ae60',
                    color: '#fff',
                    fontWeight: 600,
                    cursor:
                      bulkMemberUpdating || bulkActiveSeasons.length === 0
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: bulkMemberUpdating || bulkActiveSeasons.length === 0 ? 0.6 : 1,
                  }}
                >
                  {bulkMemberUpdating ? 'Mise à jour…' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && editingUser && (
          <div className="modal-overlay" onClick={() => {
            if (!isEditMode) {
              setShowModal(false);
              resetForm();
            }
          }}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{isEditMode ? 'Modifier l\'utilisateur' : 'Détails de l\'utilisateur'}</h2>
                <button className="btn-close" onClick={() => {
                  setShowModal(false);
                  setIsEditMode(false);
                  resetForm();
                }}>×</button>
              </div>

              {!isEditMode ? (
                <div className="subscriber-details">
                  {/* Personal Info */}
                  <section className="detail-section">
                    <h3>Informations personnelles</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Civilité:</label>
                        <span>{editingUser.civilite || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Nom:</label>
                        <span>{editingUser.lastName || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Prénom:</label>
                        <span>{editingUser.firstName || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Sexe:</label>
                        <span>{editingUser.sexe || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date de naissance:</label>
                        <span>
                          {editingUser.dateOfBirth
                            ? formatBirthDateDisplayUtc(editingUser.dateOfBirth, 'fr-CH') || '—'
                            : '—'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Lieu de naissance:</label>
                        <span>
                          {editingUser.birthPlace?.city || ''}
                          {editingUser.birthPlace?.canton && ` (${editingUser.birthPlace.canton})`}, 
                          {editingUser.birthPlace?.country || ''}
                        </span>
                      </div>
                    </div>
                  </section>

                  {/* Contact */}
                  <section className="detail-section">
                    <h3>Contact</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Email:</label>
                        <span>{editingUser.email}</span>
                      </div>
                      <div className="detail-item">
                        <label>Téléphone:</label>
                        <span>{formatPhoneDisplay(editingUser.phone)}</span>
                      </div>
                    </div>
                  </section>

                  {/* Address */}
                  <section className="detail-section">
                    <h3>Adresse de résidence</h3>
                    <div className="detail-grid">
                      <div className="detail-item full-width">
                        <label>Rue:</label>
                        <span>{editingUser.address?.street} {editingUser.address?.streetNumber || ''}</span>
                      </div>
                      <div className="detail-item">
                        <label>Code postal:</label>
                        <span>{editingUser.address?.postalCode || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Ville:</label>
                        <span>{editingUser.address?.city || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Canton:</label>
                        <span>{editingUser.address?.canton || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Pays:</label>
                        <span>{editingUser.address?.country || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Codice fiscale:</label>
                        <span>{editingUser.codiceFiscale || '—'}</span>
                      </div>
                    </div>
                  </section>

                  {/* Identity Document */}
                  {editingUser.identity && (
                    <section className="detail-section">
                      <h3>Document d'identité</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Type:</label>
                          <span>{editingUser.identity.type || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Numéro:</label>
                          <span>{editingUser.identity.number || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Date d'expiration:</label>
                          <span>{formatDate(editingUser.identity.expiryDate)}</span>
                        </div>
                        <div className="detail-item">
                          <label>Date d&apos;émission:</label>
                          <span>{formatDate(editingUser.identity.issueDate)}</span>
                        </div>
                        <div className="detail-item">
                          <label>Canton/Département d&apos;émission:</label>
                          <span>{editingUser.identity.issueCanton || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Commune/localité d&apos;émission:</label>
                          <span>{editingUser.identity.issueCity || '—'}</span>
                        </div>
                      </div>
                    </section>
                  )}

                  <section className="detail-section">
                    <h3>Codes {MEMBERSHIP_SEASON}</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>e-card:</label>
                        <span>{getMembershipCodesForSeason(editingUser, MEMBERSHIP_SEASON).ecardCode || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Code PNR:</label>
                        <span>{getMembershipCodesForSeason(editingUser, MEMBERSHIP_SEASON).pnrCode || '—'}</span>
                      </div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h3>Juventus Card</h3>
                    <div className="form-group" style={{ maxWidth: '480px' }}>
                      <label>Numéro Juventus Card</label>
                      <input
                        type="text"
                        readOnly
                        value={formData.juventusFanCard?.number ?? ''}
                        placeholder="—"
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          backgroundColor: '#f4f6f8',
                          color: '#333',
                          cursor: 'default',
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ maxWidth: '480px' }}>
                      <label>Échéance</label>
                      <input
                        type="date"
                        readOnly
                        value={formData.juventusFanCard?.expiryDate ?? ''}
                        style={{
                          width: '100%',
                          maxWidth: '280px',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          backgroundColor: '#f4f6f8',
                          color: '#333',
                          cursor: 'default',
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ maxWidth: '480px' }}>
                      <label>PIN Webticket</label>
                      <input
                        type="text"
                        readOnly
                        value={formData.juventusFanCard?.webticketPIN ?? ''}
                        placeholder="—"
                        autoComplete="off"
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          backgroundColor: '#f4f6f8',
                          color: '#333',
                          cursor: 'default',
                        }}
                      />
                      <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666', marginBottom: 0 }}>
                        Pour modifier ces champs, utilisez le bouton « Modifier » ci-dessous.
                      </p>
                    </div>
                  </section>

                  {renderConsentsSection(editingUser)}
                  {renderParentSection(editingUser)}

                  <section className="detail-section">
                    <h3>Abonnement (place)</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Tribune:</label>
                        <span>{editingUser.subscription?.tribune || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Secteur:</label>
                        <span>{editingUser.subscription?.settore || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Rangée:</label>
                        <span>{editingUser.subscription?.fila || '—'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Place:</label>
                        <span>{editingUser.subscription?.posto || '—'}</span>
                      </div>
                    </div>
                  </section>

                  {/* Account Info */}
                  <section className="detail-section">
                    <h3>Informations du compte</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Rôle:</label>
                        <span>
                          <span className={`role ${editingUser.role}`}>
                            {editingUser.role === 'admin' ? 'Admin' : 'Utilisateur'}
                          </span>
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Membre actif:</label>
                        <span>
                          <span className={`status ${editingUser.isMember ? 'published' : 'draft'}`}>
                            {editingUser.isMember ? 'Oui' : 'Non'}
                          </span>
                        </span>
                      </div>
                      <div className="detail-item full-width">
                        <label>Saisons actives:</label>
                        <span>{formatActiveSeasonsCell(editingUser)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date d'inscription:</label>
                        <span>{formatDate(editingUser.createdAt)}</span>
                      </div>
                    </div>
                  </section>

                  {MEMBER_ACTIVE_SEASONS.map((seasonLabel) => {
                    const seasonFlags = getMembershipFlagsForSeason(editingUser, seasonLabel);
                    const fields = getMembershipFieldsForSeason(seasonLabel);
                    return (
                      <section className="detail-section" key={seasonLabel}>
                        <h3>Type d&apos;adhésion {seasonLabel}</h3>
                        <div className="detail-grid">
                          {fields.map((field) => (
                            <div className="detail-item" key={field.key}>
                              <label>{field.label}:</label>
                              <span style={{ color: seasonFlags[field.key] ? '#27ae60' : '#e74c3c' }}>
                                {formatBoolean(seasonFlags[field.key])}
                              </span>
                            </div>
                          ))}
                        </div>
                      </section>
                    );
                  })}

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-submit"
                      onClick={() => setIsEditMode(true)}
                    >
                      <FaEdit /> Modifier
                    </button>
                    <button 
                      type="button" 
                      className="btn-delete"
                      onClick={handleDelete}
                    >
                      <FaTrash /> Supprimer
                    </button>
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="admin-form" style={{ padding: '1.5rem' }}>
                  <div className="detail-section">
                    <h3>Informations personnelles</h3>
                    <div className="form-group">
                      <label>Civilité *</label>
                      <select
                        name="civilite"
                        value={formData.civilite}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Sélectionner</option>
                        <option value="M">M</option>
                        <option value="Mme">Mme</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Prénom *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Nom *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Sexe *</label>
                      <select
                        name="sexe"
                        value={formData.sexe}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Sélectionner</option>
                        <option value="Homme">Homme</option>
                        <option value="Femme">Femme</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Date de naissance *</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Pays de naissance</label>
                      <input
                        type="text"
                        name="birthPlace.country"
                        value={formData.birthPlace.country}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Canton/Province/Département de naissance</label>
                      <input
                        type="text"
                        name="birthPlace.canton"
                        value={formData.birthPlace.canton}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Ville de naissance</label>
                      <input
                        type="text"
                        name="birthPlace.city"
                        value={formData.birthPlace.city}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Contact</h3>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Téléphone *</label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Adresse de résidence</h3>
                    <div className="form-group">
                      <label>Rue *</label>
                      <input
                        type="text"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Numéro *</label>
                      <input
                        type="text"
                        name="address.streetNumber"
                        value={formData.address.streetNumber}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Code postal *</label>
                      <input
                        type="text"
                        name="address.postalCode"
                        value={formData.address.postalCode}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Ville *</label>
                      <input
                        type="text"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Canton</label>
                      <input
                        type="text"
                        name="address.canton"
                        value={formData.address.canton}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Pays *</label>
                      <input
                        type="text"
                        name="address.country"
                        value={formData.address.country}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Codice fiscale{isResidentItaly(formData.address.country) ? ' *' : ''}
                      </label>
                      <input
                        type="text"
                        name="codiceFiscale"
                        value={formData.codiceFiscale}
                        onChange={handleChange}
                        required={isResidentItaly(formData.address.country)}
                        placeholder="Codice fiscale (obligatoire si résidence en Italie)"
                      />
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Document d'identité</h3>
                    <div className="form-group">
                      <label>Type</label>
                      <select
                        name="identity.type"
                        value={formData.identity.type}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionner</option>
                        <option value="Carte d'identité">Carte d'identité</option>
                        <option value="Passeport">Passeport</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Numéro</label>
                      <input
                        type="text"
                        name="identity.number"
                        value={formData.identity.number}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Date d'expiration</label>
                      <input
                        type="date"
                        name="identity.expiryDate"
                        value={formData.identity.expiryDate}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Date d&apos;émission</label>
                      <input
                        type="date"
                        name="identity.issueDate"
                        value={formData.identity.issueDate}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Canton/Département d&apos;émission</label>
                      <input
                        type="text"
                        name="identity.issueCanton"
                        value={formData.identity.issueCanton}
                        onChange={handleChange}
                        placeholder="VD, VS, GE, 75, MI…"
                      />
                    </div>

                    <div className="form-group">
                      <label>Commune/localité d&apos;émission</label>
                      <input
                        type="text"
                        name="identity.issueCity"
                        value={formData.identity.issueCity}
                        onChange={handleChange}
                        placeholder="Exemple : Lausanne"
                      />
                    </div>
                  </div>

                  {renderConsentsSection(editingUser)}
                  {renderParentSection(editingUser)}

                  <div className="detail-section">
                    <h3>Abonnement (place)</h3>
                    <div className="form-group checkbox">
                      <label>
                        <input
                          type="checkbox"
                          name="subscription.has"
                          checked={formData.subscription.has}
                          onChange={handleChange}
                        />
                        Abonné (a un abonnement)
                      </label>
                    </div>
                    <div className="form-group">
                      <label>Tribune</label>
                      <select
                        name="subscription.tribune"
                        value={formData.subscription.tribune}
                        onChange={handleChange}
                      >
                        <option value="">—</option>
                        {SUBSCRIPTION_TRIBUNE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                        {formData.subscription.tribune &&
                          !SUBSCRIPTION_TRIBUNE_OPTIONS.includes(formData.subscription.tribune) && (
                            <option value={formData.subscription.tribune}>
                              {formData.subscription.tribune}
                            </option>
                          )}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Secteur</label>
                      <input
                        type="text"
                        name="subscription.settore"
                        value={formData.subscription.settore}
                        onChange={handleChange}
                        placeholder="101-126, 201-232"
                      />
                    </div>
                    <div className="form-group">
                      <label>Rangée</label>
                      <input
                        type="text"
                        name="subscription.fila"
                        value={formData.subscription.fila}
                        onChange={handleChange}
                        placeholder="Numéro de rangée"
                      />
                    </div>
                    <div className="form-group">
                      <label>Place</label>
                      <input
                        type="text"
                        name="subscription.posto"
                        value={formData.subscription.posto}
                        onChange={handleChange}
                        placeholder="1-58"
                      />
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Juventus Card</h3>
                    <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#666' }}>
                      Numéro, échéance et PIN Webticket sont optionnels.
                    </p>
                    <div className="form-group">
                      <label>Numéro Juventus Card</label>
                      <input
                        type="text"
                        name="juventusFanCard.number"
                        value={formData.juventusFanCard.number}
                        onChange={handleChange}
                        placeholder="Numéro sur la carte"
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label>Échéance</label>
                      <input
                        type="date"
                        name="juventusFanCard.expiryDate"
                        value={formData.juventusFanCard.expiryDate || ''}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>PIN Webticket</label>
                      <input
                        type="text"
                        name="juventusFanCard.webticketPIN"
                        value={formData.juventusFanCard.webticketPIN || ''}
                        onChange={handleChange}
                        placeholder="PIN Webticket"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Informations du compte</h3>
                    <div className="form-group">
                      <label>Rôle *</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                      >
                        <option value="user">Utilisateur</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>

                    <div className="form-group checkbox">
                      <label>
                        <input
                          type="checkbox"
                          name="isMember"
                          checked={formData.isMember}
                          onChange={handleChange}
                        />
                        Membre actif
                      </label>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Saisons actives</h3>
                    <p style={{ marginBottom: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
                      Cochez les saisons où ce membre est ou était actif.
                    </p>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '1rem',
                      }}
                    >
                      {MEMBER_ACTIVE_SEASONS.map((seasonLabel) => (
                        <div className="form-group checkbox" key={seasonLabel}>
                          <label>
                            <input
                              type="checkbox"
                              name={`activeSeasons.${seasonLabel}`}
                              checked={(formData.activeSeasons || []).includes(seasonLabel)}
                              onChange={handleChange}
                            />
                            {seasonLabel}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {MEMBER_ACTIVE_SEASONS.map((seasonLabel) => (
                    <div className="detail-section" key={`membership-codes-edit-${seasonLabel}`}>
                      <h3>Codes {seasonLabel}</h3>
                      <div className="form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group">
                          <label>e-card</label>
                          <input
                            type="text"
                            name={`membershipCodesBySeason.${seasonLabel}.ecardCode`}
                            value={formData.membershipCodesBySeason?.[seasonLabel]?.ecardCode || ''}
                            onChange={handleChange}
                            placeholder="JOFC-XXXXXXXX"
                          />
                        </div>
                        <div className="form-group">
                          <label>Code PNR (abonnés)</label>
                          <input
                            type="text"
                            name={`membershipCodesBySeason.${seasonLabel}.pnrCode`}
                            value={formData.membershipCodesBySeason?.[seasonLabel]?.pnrCode || ''}
                            onChange={handleChange}
                            placeholder="JX...."
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {MEMBER_ACTIVE_SEASONS.map((seasonLabel) => (
                    <div className="detail-section" key={`membership-edit-${seasonLabel}`}>
                      <h3>Type d&apos;adhésion {seasonLabel}</h3>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '1rem',
                        }}
                      >
                        {getMembershipFieldsForSeason(seasonLabel).map((field) => (
                          <div className="form-group checkbox" key={field.key}>
                            <label>
                              <input
                                type="checkbox"
                                name={`membershipFlagsBySeason.${seasonLabel}.${field.key}`}
                                checked={formData.membershipFlagsBySeason?.[seasonLabel]?.[field.key] || false}
                                onChange={handleChange}
                              />
                              {field.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="form-actions">
                    <button type="submit" className="btn-submit">
                      Enregistrer les modifications
                    </button>
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={() => setIsEditMode(false)}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        <AdminAddUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={() => {
            fetchUsers();
            alert(
              "Utilisateur créé avec succès. Il apparaît dans « Nouveaux membres » tant que son abonnement n'est pas validé."
            );
          }}
        />
      </div>
    </div>
  );
};

export default AdminUsers;
